import { useEffect } from 'react';
import { useLocation } from 'react-router';
import Navbar from '@components/landing/Navbar';
import Footer from '@components/landing/Footer';
import ScrollToTop from '@components/ScrollToTop';
import AboutHero from '@components/about/Hero';
import AboutIntro from '@components/about/Intro';
import AboutMissionVision from '@components/about/MissionVision';
import AboutOurPrograms from '@components/about/OurPrograms';
import AboutTeam from '@components/about/Team';
import VolunteersCTA from '@components/landing/VolunteersCTA';
import Programs from '@components/landing/Programs';
import Stories from '@components/landing/Stories';
import Newsletter from '@components/landing/Newsletter';
import Testimonials from '@components/landing/Testimonials';
import Partners from '@components/landing/Partners';
import Feedback from '@components/landing/Feedback';
import Seo from '@components/Seo';
import JsonLd from '@components/JsonLd';
import { organisationSchema } from '@/lib/schema';

/** Height of the fixed navbar — sections are offset by this when deep-linked. */
const NAV_OFFSET = 90;

/**
 * Scroll to the section named in the URL hash (e.g. /about#team). The page scrolls
 * inside the custom `.page-scroll-root` container, so the browser's native hash
 * scroll can't reach it — we scroll the container manually once layout settles.
 */
function useHashScroll() {
    const { hash } = useLocation();
    useEffect(() => {
        const id = hash.replace('#', '');
        if (!id) return;
        const timer = setTimeout(() => {
            const el = document.getElementById(id);
            const root = document.querySelector<HTMLElement>('.page-scroll-root');
            if (!el || !root) return;
            const top = el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop - NAV_OFFSET;
            root.scrollTo({ top, behavior: 'smooth' });
        }, 280);
        return () => clearTimeout(timer);
    }, [hash]);
}

export default function About() {
    useHashScroll();
    return (
        <div className="page-scroll-root bg-white">
            <Navbar solid />
            <JsonLd id="organisation" data={organisationSchema()} />
            <Seo
                title="About Us"
                description="Who we are, how we work and the people behind Citizens’ Gavel, a Nigerian civic-tech organisation making justice faster to reach."
                canonical="/about"
            />
            <main className="w-full">
                <AboutHero />
                <div id="who-we-are">
                    <AboutIntro />
                </div>
                <AboutMissionVision />
                <AboutOurPrograms />
                <div id="team">
                    <AboutTeam />
                </div>
                <div id="volunteers">
                    <VolunteersCTA />
                </div>
                <Programs />
                <Stories />
                <Testimonials />
                <Newsletter />
                <div id="partners">
                    <Partners />
                </div>
                <Feedback />
            </main>
            <Footer />
            <ScrollToTop />
        </div>
    );
}
